import React from 'react';

/** Text only — never hand an object to React as a child. */
const txt = (v) => (v == null || typeof v === 'object' ? '' : String(v));
const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : txt(v));


export function normalizeSection(item) {
  if (!item || typeof item !== 'object') return { act: { title: txt(item) }, sec: {} };
  if (item.section && typeof item.section === 'object') {
    const a = item.act && typeof item.act === 'object' ? item.act : { title: txt(item.act), key: item.actKey };
    return { act: a, sec: item.section };
  }
  return {
    act: {
      key: item.actKey || item.key,
      title: typeof item.act === 'string' ? item.act : (item.actName || item.actTitle || item.title),
      url: item.actUrl,
      in_force: item.in_force,
    },
    sec: {
      number: item.number || (typeof item.section === 'string' ? item.section : null),
      unit: item.unit,
      heading: item.heading || item.sectionTitle,
      text: item.text || item.sectionText || item.content,
      url: item.url,
      offence: item.offence || item.classification,
      children: item.children,
      equivalents: item.equivalents,
      relation: item.relation,
      direction: item.direction,
    },
  };
}

const unitLabel = (u) => ({ s: 'Sec.', a: 'Art.', r: 'Rule', o: 'Order', sch: 'Sch.' }[u] || 'Sec.');

function Offence({ o }) {
  if (!o || typeof o !== 'object') return null;
  const list = Array.isArray(o.offences) ? o.offences : null;
  if (list && list.length) {
    return (
      <div className="table-responsive mt-2">
        <table className="table ec-table mb-0 small">
          <thead><tr><th>Offence</th><th>Cognizable</th><th>Bailable</th><th>Punishment</th><th>Triable by</th></tr></thead>
          <tbody>
            {list.map((x, i) => (
              <tr key={i}>
                <td>{txt(x.offence)}</td><td>{yesNo(x.cognizable)}</td><td>{yesNo(x.bailable)}</td>
                <td>{txt(x.punishment)}</td><td>{txt(x.triable_by || x.triableBy)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  const chips = [
    o.cognizable != null && `${o.cognizable ? 'Cognizable' : 'Non-cognizable'}`,
    o.bailable != null && `${o.bailable ? 'Bailable' : 'Non-bailable'}`,
    txt(o.triable_by || o.triableBy) && `Triable by ${txt(o.triable_by || o.triableBy)}`,
  ].filter(Boolean);
  const other = Object.entries(o).filter(([k, v]) => !['cognizable', 'bailable', 'triable_by', 'triableBy', 'punishment', 'offences'].includes(k) && v != null && typeof v !== 'object');
  return (
    <div className="mt-2">
      <div className="d-flex flex-wrap gap-2">
        {chips.map((c) => <span key={c} className="ec-badge ec-badge-grey">{c}</span>)}
        {other.map(([k, v]) => <span key={k} className="ec-badge ec-badge-grey">{k.replace(/_/g, ' ')}: {yesNo(v)}</span>)}
      </div>
      {txt(o.punishment) && <div className="small mt-1"><strong>Punishment:</strong> {txt(o.punishment)}</div>}
    </div>
  );
}

/**
 * One section, fully shown: act, number, heading, bare text, offence classification,
 * sub-sections and old ↔ new equivalents. onOpen(key, number) opens an equivalent.
 */
export default function SectionCard({ item, onOpen }) {
  const { act, sec } = normalizeSection(item);
  const children = Array.isArray(sec.children) ? sec.children : [];
  const equivalents = Array.isArray(sec.equivalents) ? sec.equivalents : [];
  return (
    <div className="ec-result">
      <div className="d-flex flex-wrap align-items-center gap-2">
        {txt(sec.number) && <span className="ec-badge ec-badge-yellow">{unitLabel(sec.unit)} {txt(sec.number)}</span>}
        <span className="ec-result-title">{txt(act.title) || txt(act.key) || 'Section'}</span>
        {txt(act.key) && txt(act.title) && <span className="ec-mono small ec-muted">{txt(act.key)}</span>}
        {act.in_force === false && <span className="ec-badge ec-badge-orange">Repealed</span>}
        {act.in_force === true && <span className="ec-badge ec-badge-green">In force</span>}
        {txt(sec.relation) && <span className="ec-badge ec-badge-grey">{txt(sec.relation)}{txt(sec.direction) ? ` · ${txt(sec.direction)}` : ''}</span>}
      </div>
      {txt(sec.heading) && <div className="fw-semibold mt-1">{txt(sec.heading)}</div>}
      {txt(sec.text) && <div className="mt-1 ec-prewrap ec-section-text">{txt(sec.text)}</div>}
      <Offence o={sec.offence} />
      {children.length > 0 && (
        <div className="mt-2">
          {children.map((c, i) => (
            <div key={i} className="small ec-prewrap mt-1">
              {txt(c && c.number) && <strong>({txt(c.number)}) </strong>}{txt(c && (c.heading || c.text)) || txt(c)}
            </div>
          ))}
        </div>
      )}
      {equivalents.length > 0 && (
        <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
          <span className="small ec-muted">Equivalent:</span>
          {equivalents.map((e, i) => {
            const label = `${txt(e.title) || txt(e.key)} ${unitLabel(e.unit)} ${txt(e.number)}`;
            const meta = [txt(e.relation), txt(e.direction)].filter(Boolean).join(' · ');
            return onOpen && e.key && e.number
              ? <button key={i} type="button" className="ec-pill" onClick={() => onOpen(e.key, e.number)} title={meta}>{label}{meta ? ` (${meta})` : ''}</button>
              : <span key={i} className="ec-tag" title={meta}>{label}{meta ? ` (${meta})` : ''}</span>;
          })}
        </div>
      )}
      <div className="d-flex flex-wrap gap-3 mt-2 small">
        {txt(sec.url) && <a className="ec-link" href={sec.url} target="_blank" rel="noopener noreferrer">Section on India Code</a>}
        {txt(act.url) && <a className="ec-link" href={act.url} target="_blank" rel="noopener noreferrer">Full act</a>}
      </div>
    </div>
  );
}